<template>
    <USlideover prevent-close :model-value="open" @update:model-value="$emit('update:open', $event)" :ui="{ width: 'max-w-3xl' }">
        <UCard>
            <template #header>
                <div class="flex items-center justify-between">
                    <h3 class="text-base font-medium leading-6 text-gray-800">Create Provider Invoice</h3>
                </div>
            </template>

            <div class="space-y-6">
                <UFormGroup name="provider_id" label="Provider" required>
                    <UInputMenu
                        v-model="provider.selected"
                        @update:model-value="onProviderSelected($event)"
                        :loading="provider.searching"
                        :search="searchProvider"
                        placeholder="Choose a provider..."
                        option-attribute="name"
                        size="xl"
                        trailing
                        by="id"
                    />
                </UFormGroup>
                <template v-if="provider.selected">
                    <div v-if="loadingTransports" class="flex items-center justify-center space-x-2 p-6 rounded-md border border-dashed border-strong border-gray-300">
                        <UIcon name="i-ph-circle-dashed" class="size-4 text-gray-700 animate-spin-slow" />
                        <span class="text-sm text-gray-500">Loading uninvoiced transports...</span>
                    </div>
                    <div v-else-if="!transports.length" class="flex flex-col items-center justify-center space-y-2 p-6 rounded-md border border-dashed border-strong border-gray-300">
                        <UIcon name="i-ph-ambulance-duotone" class="size-6 text-gray-300" />
                        <span class="text-sm text-gray-400 italic">No uninvoiced transports.</span>
                    </div>
                    <div v-else class="space-y-4">
                        <div class="space-y-2">
                            <p class="text-sm font-medium">Transports</p>
                            <UTable class="border rounded-md min-h-[300px]" :rows="paginatedTransports" :columns="tableColumns">
                                <template #number-data="{ row }">
                                    <span class="font-mono text-sm">{{ row.number?.toString().padStart(6, '0') }}</span>
                                </template>
                                <template #customer-data="{ row }">
                                    <p class="text-sm">
                                        {{ [row.customer?.first_name, row.customer?.middle_name, row.customer?.last_name].filter(Boolean).join(' ') }}
                                    </p>
                                </template>
                                <template #scheduled-data="{ row }">
                                    <TableDateCell :date="row.scheduled_date" without-time class="text-sm" />
                                </template>
                                <template #price-data="{ row }">
                                    <p class="font-mono text-sm text-right">${{ row.price ?? 0 }}</p>
                                </template>
                                <template #commission-data="{ row }">
                                    <p class="font-mono text-sm text-right">
                                        ${{ ((row.price || 0) * commissionPercentage / 100).toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
                                    </p>
                                </template>
                            </UTable>

                            <TablePagination :pagination="pagination" class="shrink-0" />
                        </div>
                    </div>
                </template>
            </div>

            <template #footer>
                <div class="flex items-center justify-between space-x-2">
                    <div v-if="provider.selected && transports.length" class="flex items-center space-x-4">
                        <Field label="Gross Total" class="leading-4">
                            <span class="font-mono">
                                ${{ grossTotal.toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
                            </span>
                        </Field>
                        <Field :label="'Commission (' + commissionPercentage + '%)'" class="leading-4">
                            <span class="font-mono">
                                ${{ commissionTotal.toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
                            </span>
                        </Field>
                    </div>
                    <div v-else></div>
                    <div class="flex items-center justify-end space-x-2">
                        <UButton @click="dismiss()" size="lg" color="white">
                            Cancel
                        </UButton>
                        <UButton @click="confirm()" size="lg" :loading="confirming" :disabled="!canCreate" color="black">
                            Create
                        </UButton>
                    </div>
                </div>
            </template>
        </UCard>
    </USlideover>
</template>

<script setup lang="ts">
import type { Provider } from '~/types/provider'

const emit = defineEmits<{
    'update:open': [boolean]
    'reload': [boolean]
}>()

const props = defineProps<{
    open: boolean
}>()

const toast = useToast()
const client = useSupabaseClient()
const pagination = usePagination(20)

const loadingTransports = ref(false)
const confirming = ref(false)

const provider = ref<{
    searching: boolean
    selected?: Partial<Provider>
}>({
    searching: false,
})

const transports = ref<any[]>([])
const commissionPercentage = ref(0)

const tableColumns = [
    { key: 'number', label: '#' },
    { key: 'customer', label: 'Patient' },
    { key: 'scheduled', label: 'Date' },
    { key: 'price', label: 'Price' },
    { key: 'commission', label: 'Commission' },
]

const canCreate = computed(() => {
    return !!provider.value.selected?.id && transports.value.length > 0
})

const paginatedTransports = computed(() => {
    const start = pagination.page * pagination.limit
    const end = (pagination.page + 1) * pagination.limit
    return transports.value.slice(start, end)
})

const grossTotal = computed(() => transports.value.reduce((sum, t) => sum + (t.price || 0), 0))
const commissionTotal = computed(() => grossTotal.value * commissionPercentage.value / 100)

watch(() => props.open, (open) => {
    if (open) reset()
})

function onProviderSelected(p: any) {
    commissionPercentage.value = p?.commission_percentage ?? 0
    fetchTransports()
}

async function searchProvider(q: string) {
    provider.value.searching = true

    const { data, error } = await client
        .from('providers')
        .select('id, name, commission_percentage')
        .is('deleted_at', null)
        .ilike('name', `%${q}%`)
        .order('name')

    provider.value.searching = false
    if (error) return []
    return data
}

async function fetchTransports() {
    loadingTransports.value = true
    transports.value = []

    if (provider.value.selected?.id) {
        const { data } = await client.rpc('get_uninvoiced_transports', {
            p_provider_id: provider.value.selected.id,
        } as never)

        if (data) {
            transports.value = data as any[]
            pagination.setTotal(transports.value.length)
        }
    } else {
        pagination.setTotal(undefined)
    }

    loadingTransports.value = false
}

async function confirm() {
    if (!provider.value.selected?.id) return

    confirming.value = true

    const { data: lastInvoice } = await client.from('provider_invoices')
        .select('number')
        .eq('provider_id', provider.value.selected.id)
        .order('number', { ascending: false })
        .limit(1)
        .maybeSingle()

    const nextNumber = lastInvoice?.number ? Number(lastInvoice.number) + 1 : 1

    const { data: invoice, error } = await client.from('provider_invoices')
        .insert({
            number: nextNumber,
            provider_id: provider.value.selected.id,
            commission_percentage: commissionPercentage.value,
        } as never)
        .select('id')
        .single()

    if (!invoice || error) {
        toast.add({
            title: 'Could not create invoice',
            description: 'Please try again later.',
            color: 'red',
            icon: 'i-ph-warning-duotone'
        })
        confirming.value = false
        return dismiss()
    }

    const transportIds = transports.value.map(t => t.id)
    await client.from('transports')
        .update({ invoice_id: invoice.id } as never)
        .in('id', transportIds)

    confirming.value = false
    dismiss(true)
}

function dismiss(reload: boolean = false) {
    emit('update:open', false)
    if (reload) {
        emit('reload', true)
    }
}

function reset() {
    loadingTransports.value = false
    confirming.value = false
    pagination.setTotal(undefined)
    pagination.page = 0
    transports.value = []
    commissionPercentage.value = 0

    provider.value = {
        searching: false,
        selected: undefined,
    }
}
</script>
